import React, { useMemo, useState } from 'react';
import { Card, CardBody, Badge, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { copyTextToClipboard } from '../../helper/order_address_helper';
import ShipmentTrackingStatusBadge from './ShipmentTrackingStatusBadge';
import {
  formatEtaDate,
  formatTrackingTimestampIst,
  getExpectedDeliveryDate,
  getLastShipmentUpdateTime,
  getLatestTrackingStatusCode,
  getOrderAwb,
  getOrderCourierName,
  getShipmentLastLocation,
  getShipmentListWarnings,
  getShipmentStatusRaw,
  getShipmentStepperSteps,
  getTimelineEventIcon,
  isShipmentNotStarted,
  normalizeTrackingHistory,
} from '../../helper/shipment_tracking_helper';

const TIMELINE_COLLAPSE_THRESHOLD = 6;

async function copyAwb(awb) {
  if (!awb) return;
  try {
    await copyTextToClipboard(awb);
    toast.success('AWB copied');
  } catch {
    toast.error('Could not copy AWB');
  }
}

export default function OrderShipmentTracking({ order }) {
  const [showAllTimeline, setShowAllTimeline] = useState(false);

  const notStarted = isShipmentNotStarted(order);
  const statusRaw = getShipmentStatusRaw(order);
  const trackingCode = getLatestTrackingStatusCode(order);
  const awb = getOrderAwb(order);
  const courier = getOrderCourierName(order);
  const lastUpdated = getLastShipmentUpdateTime(order);
  const etaLabel = formatEtaDate(getExpectedDeliveryDate(order));
  const lastLocation = getShipmentLastLocation(order);
  const warnings = getShipmentListWarnings(order);
  const stepperSteps = useMemo(() => getShipmentStepperSteps(order), [order]);
  const timeline = useMemo(() => normalizeTrackingHistory(order), [order]);
  const visibleTimeline = showAllTimeline
    ? timeline
    : timeline.slice(0, TIMELINE_COLLAPSE_THRESHOLD);

  if (notStarted) {
    return (
      <Card className="card-one border shadow-sm order-shipment-card mb-3">
        <CardBody>
          <div className="d-flex align-items-start gap-3">
            <div className="order-shipment-card__empty-icon">
              <i className="ri-truck-line" aria-hidden="true" />
            </div>
            <div>
              <h6 className="fw-semibold mb-1">Shipment tracking</h6>
              <p className="text-muted small mb-2">
                This order has not been handed to a courier yet. Tracking will appear here once
                the shipment is packed and an AWB is generated.
              </p>
              {order?.shipcluesOrderId || order?.shipcluesShipmentId ? (
                <p className="small mb-0 text-muted">
                  Shipclues reference on file — refresh after the courier picks up the parcel.
                </p>
              ) : null}
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="card-one border shadow-sm order-shipment-card mb-3">
      <CardBody>
        <div className="order-shipment-card__header">
          <div>
            <h6 className="fw-semibold mb-2">
              <i className="ri-map-pin-time-line me-1" />
              Shipment tracking
            </h6>
            <div className="d-flex flex-wrap align-items-center gap-2 small">
              <span className="text-muted">Courier:</span>
              <span className="fw-medium">{courier}</span>
              {awb ? (
                <>
                  <span className="text-muted ms-1">AWB:</span>
                  <code className="order-shipment-card__awb">{awb}</code>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="py-0 px-2"
                    aria-label="Copy AWB"
                    onClick={() => copyAwb(awb)}
                  >
                    <i className="ri-file-copy-line me-1" />
                    Copy
                  </Button>
                </>
              ) : (
                <span className="text-muted">AWB pending</span>
              )}
            </div>
          </div>
          <div className="order-shipment-card__meta text-md-end">
            <div className="d-flex flex-wrap justify-content-md-end align-items-center gap-1 mb-1">
              <ShipmentTrackingStatusBadge order={order} />
              {warnings.map((w) => (
                <Badge
                  key={w.code}
                  bg={w.tone || (w.code === 'stuck' ? 'warning' : 'danger')}
                  text={w.code === 'stuck' ? 'dark' : undefined}
                >
                  {w.code === 'exception' ? (
                    <i className="ri-error-warning-line me-1" aria-hidden="true" />
                  ) : null}
                  {w.label}
                </Badge>
              ))}
            </div>
            {trackingCode || statusRaw ? (
              <div className="text-muted small">
                Courier code: {trackingCode || statusRaw}
              </div>
            ) : null}
            {lastLocation ? (
              <div className="text-muted small">
                <i className="ri-map-pin-line me-1" />
                {lastLocation}
              </div>
            ) : null}
            <div className="text-muted small">
              Last updated: {formatTrackingTimestampIst(lastUpdated)}
            </div>
            {etaLabel ? (
              <div className="text-muted small fw-medium">ETA: {etaLabel}</div>
            ) : null}
            {order?.shipcluesOrderId || order?.shipcluesShipmentId ? (
              <div className="text-muted small mt-1" style={{ wordBreak: 'break-all' }}>
                {order.shipcluesOrderId ? <div>Shipclues order: {order.shipcluesOrderId}</div> : null}
                {order.shipcluesShipmentId ? (
                  <div>Shipclues shipment: {order.shipcluesShipmentId}</div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="order-shipment-stepper" role="list" aria-label="Shipment progress">
          {stepperSteps.map((step) => (
            <div
              key={step.label}
              className={`order-shipment-stepper__step order-shipment-stepper__step--${step.state}`}
              role="listitem"
            >
              <div className="order-shipment-stepper__dot" aria-hidden="true">
                {step.state === 'complete' ? (
                  <i className="ri-check-line" />
                ) : step.state === 'exception' || step.state === 'cancelled' ? (
                  <i className="ri-close-line" />
                ) : step.state === 'ndr' ? (
                  <i className="ri-error-warning-line" />
                ) : (
                  <span />
                )}
              </div>
              <span className="order-shipment-stepper__label">{step.label}</span>
            </div>
          ))}
        </div>

        {timeline.length > 0 ? (
          <div className="order-shipment-timeline mt-4">
            <h6 className="small fw-semibold text-muted text-uppercase mb-3">Activity</h6>
            <ul className="order-shipment-timeline__list list-unstyled mb-0">
              {visibleTimeline.map((event, index) => (
                <li key={`${event.timestamp}-${index}`} className="order-shipment-timeline__item">
                  <div className="order-shipment-timeline__icon" aria-hidden="true">
                    <i className={getTimelineEventIcon(event, order)} />
                  </div>
                  <div className="order-shipment-timeline__body">
                    <div className="d-flex flex-wrap justify-content-between gap-2">
                      <div>
                        <span className="fw-semibold">{event.status || 'Update'}</span>
                        {event.rawStatus ? (
                          <div className="text-muted small mt-0">
                            Code: {event.rawStatus}
                          </div>
                        ) : null}
                      </div>
                      <time className="text-muted small text-end">
                        {formatTrackingTimestampIst(event.timestamp)}
                      </time>
                    </div>
                    {event.location ? (
                      <div className="small text-muted mt-1">
                        <i className="ri-map-pin-line me-1" />
                        {event.location}
                      </div>
                    ) : null}
                    {event.remark ? (
                      <p className="small mb-0 mt-1 text-body-secondary">{event.remark}</p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
            {timeline.length > TIMELINE_COLLAPSE_THRESHOLD ? (
              <Button
                variant="link"
                size="sm"
                className="px-0 mt-2"
                onClick={() => setShowAllTimeline((v) => !v)}
              >
                {showAllTimeline
                  ? 'Show fewer events'
                  : `Show all (${timeline.length} events)`}
              </Button>
            ) : null}
          </div>
        ) : (
          <p className="text-muted small mt-3 mb-0">
            No tracking scans yet. Status reflects the latest shipment state from the courier.
          </p>
        )}
      </CardBody>
    </Card>
  );
}
